import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Ruler, X, RotateCcw, Calculator } from 'lucide-react';

interface MeasurementPoint {
  id: string;
  x: number;
  y: number;
  timestamp: number;
}

interface MeasurementLine {
  id: string;
  startPoint: MeasurementPoint;
  endPoint: MeasurementPoint;
  distance: number;
  label?: string;
}

interface MeasurementToolsProps {
  isActive: boolean;
  points: MeasurementPoint[];
  lines: MeasurementLine[];
  onToggle: () => void;
  onClear: () => void;
  formatDistance: (distance: number) => string;
  className?: string;
}

export const MeasurementTools: React.FC<MeasurementToolsProps> = ({
  isActive,
  points,
  lines,
  onToggle,
  onClear,
  formatDistance,
  className = ''
}) => {
  const totalDistance = lines.reduce((sum, line) => sum + line.distance, 0);

  return (
    <div className={`space-y-4 ${className}`}>
      {/* Measurement Toggle Button */}
      <motion.button
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        onClick={onToggle}
        className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all ${
          isActive
            ? 'bg-blue-600 text-white shadow-lg'
            : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
        }`}
      >
        <Ruler className="w-4 h-4" />
        <span>{isActive ? 'Exit Measurement' : 'Measure'}</span>
      </motion.button>

      {/* Measurement Instructions */}
      <AnimatePresence>
        {isActive && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="bg-blue-50 border border-blue-200 rounded-lg p-3"
          >
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-blue-800 font-medium mb-1">
                  Measurement Mode Active
                </p>
                <p className="text-xs text-blue-600">
                  Click on the floor plan to add measurement points
                </p>
              </div>
              {points.length > 0 && (
                <motion.button
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  onClick={onClear}
                  className="text-blue-600 hover:text-blue-800 p-1"
                >
                  <RotateCcw className="w-4 h-4" />
                </motion.button>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Measurement Summary */}
      <AnimatePresence>
        {lines.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 10 }}
            className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm"
          >
            <div className="flex items-center gap-2 mb-3">
              <Calculator className="w-4 h-4 text-gray-600" />
              <h4 className="font-medium text-gray-900">Measurements</h4>
            </div>
            
            <div className="space-y-2">
              {/* Individual Measurements */}
              {lines.map((line, index) => (
                <motion.div
                  key={line.id}
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="flex items-center justify-between text-sm"
                >
                  <span className="text-gray-600">
                    Line {index + 1}:
                  </span>
                  <span className="font-medium text-gray-900">
                    {formatDistance(line.distance)}
                  </span>
                </motion.div>
              ))}
              
              {/* Total Distance */}
              {lines.length > 1 && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: lines.length * 0.1 }}
                  className="pt-2 border-t border-gray-200 flex items-center justify-between font-medium"
                >
                  <span className="text-gray-900">Total:</span>
                  <span className="text-blue-600">
                    {formatDistance(totalDistance)}
                  </span>
                </motion.div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// Measurement Point Component for rendering on the floor plan
interface MeasurementPointProps {
  point: MeasurementPoint;
  index: number;
  isActive?: boolean;
}

export const MeasurementPointIndicator: React.FC<MeasurementPointProps> = ({
  point,
  index,
  isActive = false
}) => {
  return (
    <motion.div
      initial={{ scale: 0, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      exit={{ scale: 0, opacity: 0 }}
      transition={{ type: "spring", stiffness: 300, damping: 20 }}
      className="absolute pointer-events-none"
      style={{
        left: `${point.x}%`,
        top: `${point.y}%`,
        transform: 'translate(-50%, -50%)'
      }}
    >
      {/* Point Circle */}
      <div className={`relative w-4 h-4 rounded-full border-2 border-white shadow-lg ${
        isActive ? 'bg-blue-500' : 'bg-red-500'
      }`}>
        {/* Pulse Animation */}
        <motion.div
          animate={{ 
            scale: [1, 1.5, 1], 
            opacity: [0.7, 0, 0.7] 
          }}
          transition={{ 
            duration: 2, 
            repeat: Infinity,
            ease: "easeInOut"
          }}
          className={`absolute inset-0 rounded-full ${
            isActive ? 'bg-blue-500' : 'bg-red-500'
          }`}
        />
      </div>
      
      {/* Point Number */}
      <motion.div
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ delay: 0.2 }}
        className="absolute -top-6 left-1/2 transform -translate-x-1/2 bg-gray-900 text-white text-xs px-1.5 py-0.5 rounded font-medium"
      >
        {index + 1}
      </motion.div>
    </motion.div>
  );
};

// Measurement Line Component
interface MeasurementLineProps {
  line: MeasurementLine;
  formatDistance: (distance: number) => string;
  index: number;
}

export const MeasurementLineIndicator: React.FC<MeasurementLineProps> = ({
  line,
  formatDistance,
  index
}) => {
  const midX = (line.startPoint.x + line.endPoint.x) / 2;
  const midY = (line.startPoint.y + line.endPoint.y) / 2;

  return (
    <g>
      {/* Animated Line */}
      <motion.line
        initial={{ pathLength: 0, opacity: 0 }}
        animate={{ pathLength: 1, opacity: 1 }}
        transition={{ duration: 0.8, delay: index * 0.2 }}
        x1={`${line.startPoint.x}%`}
        y1={`${line.startPoint.y}%`}
        x2={`${line.endPoint.x}%`}
        y2={`${line.endPoint.y}%`}
        stroke="#ef4444"
        strokeWidth="2"
        strokeDasharray="5,5"
        className="drop-shadow-sm"
      />
      
      {/* Distance Label Background */}
      <motion.rect
        initial={{ scale: 0, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ delay: 0.5 + (index * 0.2) }}
        x={`${midX - 2}%`}
        y={`${midY - 1}%`}
        width="4%"
        height="2%"
        fill="white"
        stroke="#ef4444"
        strokeWidth="1"
        rx="2"
        className="drop-shadow-sm"
      />
      
      {/* Distance Label Text */}
      <motion.text
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.7 + (index * 0.2) }}
        x={`${midX}%`}
        y={`${midY + 0.3}%`}
        fill="#ef4444"
        fontSize="10"
        textAnchor="middle"
        className="font-semibold select-none"
      >
        {formatDistance(line.distance)}
      </motion.text>
    </g>
  );
};